import React, { useState, useEffect, useRef } from 'react';
import LogoIcon from '../../imgs/ico.png';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import PlanSelectionStep from './PlanSelectionStep';
import { FiArrowLeft, FiUpload, FiShield, FiDownload, FiFileText, FiCheck, FiAlertCircle, FiX } from 'react-icons/fi';
import {
  LoginContainer,
  LoginCard,
  LoginHeader,
  LoginTitle,
  LoginSubtitle,
  LoginForm,
  InputGroup,
  Label,
  Input,
  Button,
  ErrorMessage,
  SuccessMessage,
  LinkButton,
  Divider,
  Logo,
  ForgotLink,
  BackLink,
  LeftPanel
} from './styles';

const VALUE_PROPS = [
  { icon: FiDownload, title: 'Download de XMLs', desc: 'Baixe milhares de XMLs automaticamente da SEFAZ' },
  { icon: FiShield, title: 'Monitoramento eCAC', desc: 'Alertas de notificações fiscais em tempo real' },
  { icon: FiFileText, title: 'Revisor de SPEDs', desc: 'Encontre erros antes da Receita Federal' },
];

// mode: 'login' | 'register' | 'forgot' | 'reset'
const Login = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [certFile, setCertFile] = useState(null);
  const certInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    certPassword: '',
  });

  const [resetToken, setResetToken] = useState('');
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [pendingRegisterData, setPendingRegisterData] = useState(null);

  const PLAN_TO_CLASS = {
    starter: 'starter',
    pro: 'profissional',
    enterprise: 'premium',
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setMode('reset');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleCertDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file) {
      const ext = file.name.toLowerCase();
      if (ext.endsWith('.pfx') || ext.endsWith('.p12')) {
        setCertFile(file);
        setError('');
      } else {
        setError('Apenas arquivos .pfx ou .p12');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.redirected) return;
        if (!result.success) setError(result.error);
      } else if (mode === 'register') {
        if (!formData.name || !formData.email || !formData.password) {
          setError('Preencha nome, email e senha');
          setLoading(false);
          return;
        }
        if (!certFile) {
          setError('Envie seu certificado digital (.pfx)');
          setLoading(false);
          return;
        }
        if (!formData.certPassword) {
          setError('Informe a senha do certificado');
          setLoading(false);
          return;
        }
        setPendingRegisterData({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          certificateFile: certFile,
          certPassword: formData.certPassword,
        });
        setShowPlanSelector(true);
        return;
      } else if (mode === 'forgot') {
        if (!formData.email) {
          setError('Informe seu email');
          setLoading(false);
          return;
        }
        const res = await api.post('/auth/forgot-password', { email: formData.email });
        setSuccess(res.data.message || 'Verifique seu email para redefinir a senha.');
      } else if (mode === 'reset') {
        if (!formData.password || formData.password.length < 6) {
          setError('A senha deve ter pelo menos 6 caracteres');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('As senhas não coincidem');
          setLoading(false);
          return;
        }
        const res = await api.post('/auth/reset-password', { token: resetToken, newPassword: formData.password });
        setSuccess(res.data.message || 'Senha redefinida! Faça login.');
        setTimeout(() => setMode('login'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao processar requisição');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setCertFile(null);
    setFormData({ name: '', email: '', password: '', confirmPassword: '', certPassword: '' });
  };

  const handleSelectPlan = async (planSlug) => {
    if (!pendingRegisterData) return;
    try {
      const result = await register({
        ...pendingRegisterData,
        plan: planSlug,
        billingClass: PLAN_TO_CLASS[planSlug] || planSlug,
      });
      if (!result.success) {
        setError(result.error || 'Erro ao criar conta');
        setShowPlanSelector(false);
        setPendingRegisterData(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar conta');
      setShowPlanSelector(false);
      setPendingRegisterData(null);
    }
  };

  const isRegister = mode === 'register';

  if (showPlanSelector) {
    return (
      <PlanSelectionStep
        onSelectPlan={handleSelectPlan}
        onBack={() => setShowPlanSelector(false)}
      />
    );
  }

  return (
    <LoginContainer data-login>
      {/* Left panel - value props (visible only on register) */}
      {isRegister && (
        <LeftPanel>
          <div style={{ marginBottom: 48 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
              <img src={LogoIcon} alt="Vidal" style={{ width: 36, height: 36 }} />
              <span style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>Vidal</span>
            </div>
            <h1 style={{ fontSize: 32, fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: 0 }}>
              Automação fiscal<br />
              <span style={{ color: '#10b981' }}>que trabalha por você</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15, marginTop: 16, lineHeight: 1.6 }}>
              Crie sua conta com seu certificado digital e comece a automatizar em minutos.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {VALUE_PROPS.map((vp, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <vp.icon size={18} color="#10b981" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{vp.title}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{vp.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: 48, padding: '14px 18px', borderRadius: 12,
            background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)',
          }}>
            <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>14 dias grátis</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
              Sem cartão de crédito. Cancele quando quiser.
            </div>
          </div>
        </LeftPanel>
      )}

      {/* Right panel - form */}
      <LoginCard style={isRegister ? { maxWidth: 460 } : {}}>
        <LoginHeader>
          {!isRegister && (
            <Logo>
              <img src={LogoIcon} alt="Vidal" style={{ width: 48, height: 48 }} />
            </Logo>
          )}
          <LoginTitle>
            {mode === 'register' ? 'Criar conta' :
             mode === 'forgot' ? 'Recuperar senha' :
             mode === 'reset' ? 'Nova senha' :
             'Bem-vindo de volta'}
          </LoginTitle>
          <LoginSubtitle>
            {mode === 'register' ? 'Envie seu certificado digital para começar' :
             mode === 'forgot' ? 'Informe seu email e enviaremos um link' :
             mode === 'reset' ? 'Defina sua nova senha de acesso' :
             'Entre com suas credenciais'}
          </LoginSubtitle>
        </LoginHeader>

        <LoginForm onSubmit={handleSubmit}>
          {(mode === 'forgot' || mode === 'reset') && (
            <BackLink type="button" onClick={() => switchMode('login')}>
              <FiArrowLeft size={14} /> Voltar ao login
            </BackLink>
          )}

          {mode === 'register' && (
            <>
              {/* Certificate upload zone */}
              <div
                onClick={() => certInputRef.current?.click()}
                onDrop={handleCertDrop}
                onDragOver={(e) => e.preventDefault()}
                style={{
                  border: certFile ? '2px solid rgba(16,185,129,0.4)' : '2px dashed rgba(255,255,255,0.12)',
                  borderRadius: 12,
                  padding: certFile ? '12px 16px' : '20px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: certFile ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
                  transition: 'all 0.2s',
                }}
              >
                {certFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: 'rgba(16,185,129,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FiCheck size={18} color="#10b981" />
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{certFile.name}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                        {(certFile.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setCertFile(null); }}
                      style={{
                        background: 'rgba(255,255,255,0.06)', border: 'none',
                        borderRadius: 6, padding: 6, cursor: 'pointer', display: 'flex',
                      }}
                    >
                      <FiX size={14} color="rgba(255,255,255,0.5)" />
                    </button>
                  </div>
                ) : (
                  <>
                    <FiUpload size={20} color="rgba(255,255,255,0.3)" />
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.7)', marginTop: 8 }}>
                      Certificado Digital (.pfx)
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                      Arraste ou clique para selecionar
                    </div>
                  </>
                )}
                <input
                  ref={certInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={handleCertDrop}
                  style={{ display: 'none' }}
                />
              </div>

              {certFile && (
                <InputGroup>
                  <Label>Senha do certificado</Label>
                  <Input
                    type="password"
                    name="certPassword"
                    value={formData.certPassword}
                    onChange={handleChange}
                    placeholder="Senha do .pfx"
                    disabled={loading}
                    autoComplete="off"
                  />
                </InputGroup>
              )}

              <InputGroup>
                <Label>Nome completo</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  disabled={loading}
                  autoComplete="name"
                />
              </InputGroup>
            </>
          )}

          {mode !== 'reset' && (
            <InputGroup>
              <Label>Email</Label>
              <Input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="seu@email.com"
                disabled={loading}
                autoComplete="email"
              />
            </InputGroup>
          )}

          {(mode === 'login' || mode === 'register') && (
            <InputGroup>
              <Label>{mode === 'register' ? 'Crie uma senha' : 'Senha'}</Label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                disabled={loading}
                autoComplete={mode === 'login' ? "current-password" : "new-password"}
              />
            </InputGroup>
          )}

          {mode === 'login' && (
            <ForgotLink type="button" onClick={() => switchMode('forgot')}>
              Esqueci minha senha
            </ForgotLink>
          )}

          {mode === 'reset' && (
            <>
              <InputGroup>
                <Label>Nova senha</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </InputGroup>
              <InputGroup>
                <Label>Confirmar senha</Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repita a senha"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </InputGroup>
            </>
          )}

          {error && <ErrorMessage><FiAlertCircle size={14} style={{ marginRight: 6, flexShrink: 0 }} />{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Button type="submit" disabled={loading}>
            {loading ? 'Processando...' : (
              mode === 'login' ? 'Entrar' :
              mode === 'register' ? 'Criar conta grátis — 14 dias' :
              mode === 'forgot' ? 'Enviar link de recuperação' :
              'Redefinir senha'
            )}
          </Button>

          {(mode === 'login' || mode === 'register') && (
            <>
              <Divider><span>ou</span></Divider>
              <LinkButton type="button" onClick={() => switchMode(mode === 'login' ? 'register' : 'login')} disabled={loading}>
                {mode === 'login' ? 'Não tem conta? Criar com certificado' : 'Já tem conta? Fazer login'}
              </LinkButton>
            </>
          )}
        </LoginForm>

        <div style={{
          textAlign: 'center',
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.06)'
        }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
            Ao usar o Vidal, você concorda com os{' '}
            <a href="/termos" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Termos de Uso</a>
            {' '}e{' '}
            <a href="/privacidade" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Política de Privacidade</a>
          </span>
        </div>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
